import {
  batchCreateBasicField,
  batchCreateSurname,
  createFieldValue,
  createHighlightsDispatches,
  createStandardizedFieldValue,
  getAllProcessedElements,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  getNestedSubelementsByType,
  getNextUuid,
  getSupersByType,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../../shared/services/config'

/**
 * Generates default form values based on provided person IDs and shared element.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<string>} params.personIds - Array of person IDs.
 * @param {Object} params.sharedElement - The shared element object.
 * @param {string} params.fieldDisplayValue - The display value for the field.
 * @param {Array<Object>} params.persons - Array of person objects associated with the form.
 * @returns {Object} The default form values.
 */
export const getFormDefaultValues = ({ persons = [], personIds, sharedElement, fieldDisplayValue }) => {
  const formDefaultValues = {}
  if (sharedElement) {
    formDefaultValues[sharedElement.id] = fieldDisplayValue
  }
  if (personIds) {
    personIds.forEach((personId) => {
      formDefaultValues[personId] = personId
    })
  }
  formDefaultValues.persons = persons

  return formDefaultValues
}

/**
 * Returns the display field based on the provided parent and field.
 *
 * @param {Object} parent - The parent object.
 * @param {Object} field - The field object.
 * @returns {Object} The display field, which is either the parent if the field is not provided, or the field if it is provided.
 */
export const getDisplayField = (parent, field) => {
  if (parent && !field) return parent
  return field
}

/**
 * Retrieves the persons sharing a field on images.
 *
 * @param {Array<string>} allPersonIdsSharingFieldOnImages - Array of person IDs sharing the field on images.
 * @param {Array<Object>} images - Array of zion SLS images.
 * @returns {Array<Object>} Array of persons corresponding to the person IDs.
 */
export const getPersonsSharingField = (allPersonIdsSharingFieldOnImages, images) => {
  return allPersonIdsSharingFieldOnImages?.map((personId) => getNestedOrFloatingElementById(images, personId))
}

/**
 * Retrieves the super element associated with a person and a shared element.
 *
 * @param {Object} person - The person object.
 * @param {Object} sharedEl - The shared element object.
 * @returns {Object|null} The super element if found, otherwise null.
 */
export const getSuperEl = (person, sharedEl) => {
  if (sharedEl.superElements.find((se) => se.id === person.id)) {
    return person
  }
  let superEl = null
  if (sharedEl.fieldType === 'NAME_SURN') {
    // get name element that has the surname being edited as a subelement
    superEl = getNestedSubelementsByType(person, 'NAME')?.find((ne) =>
      getNestedSubelementsByType(ne, 'NAME_SURN').find((ns) => ns.id === sharedEl.id)
    )
  } else {
    sharedEl.superElements.forEach((se) => {
      const nestedSubs = getNestedSubelementsById([person], se.id)
      if (nestedSubs?.length > 0) {
        superEl = nestedSubs[0]
      }
    })
  }

  return superEl
}

/**
 * Synchronizes duplicate checkboxes with the same person ID in a form.
 *
 * When two checkboxes share the same name/value, React Hook Form creates an array.
 * This utility ensures both checkboxes stay in sync by normalizing values to:
 * - personId (string) when checked
 * - false (boolean) when unchecked
 *
 * This prevents array accumulation and maintains consistent state across duplicate checkboxes.
 *
 * @param {Object} methods - The react-hook-form methods object from useZionForm
 * @param {string} personId - The person ID (checkbox name) to synchronize
 * @param {Object|*} valuesOrFieldValue - Either the full form values object (legacy) or the specific field value (preferred). Defaults to empty object for safety.
 * @returns {{checked: boolean, onChange: Function}} Object with checked state and onChange handler
 */
export const getSyncedCheckboxProps = (methods, personId, valuesOrFieldValue = {}) => {
  const { setValue } = methods

  const currentValue =
    typeof valuesOrFieldValue === 'object' &&
    !Array.isArray(valuesOrFieldValue) &&
    valuesOrFieldValue !== null &&
    personId in valuesOrFieldValue
      ? valuesOrFieldValue[personId] // Old API: full values object
      : valuesOrFieldValue // New API: single field value

  // Determine checked state:
  // - If it's an array with items, it's checked
  // - If it's a string (the personId), it's checked
  // - Otherwise it's unchecked
  const isChecked = Array.isArray(currentValue) ? currentValue.length > 0 : Boolean(currentValue)

  /**
   * onChange handler that synchronizes both checkboxes
   * When one checkbox changes, we set the value to either the personId (checked) or false (unchecked)
   * This prevents the array behavior and keeps both checkboxes in sync
   */
  const handleChange = (event) => {
    const checked = event.target.checked
    // Set to personId (string) when checked, false (boolean) when unchecked
    // This ensures consistent value shape and prevents array accumulation
    setValue(personId, checked ? personId : false, { shouldValidate: true, shouldDirty: true })
  }

  return {
    checked: isChecked,
    onChange: handleChange,
  }
}

/**
 * Handles the submission logic for creating the dispatches for updating or creating a shared field for a person.
 *
 * 1) Create one new shared surname with the new edited values
 * 2) Attach the new surname to the checked person(s)
 * 3) Remove the old surname from their subElements
 * 4) Leave the unchecked peoples' surnames alone
 * 5) If everyone is checked, don't do any of the above and just update the value
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.values - The form values.
 * @param {Object} params.selectedPerson - The selected person object.
 * @param {Array<Object>} params.allPersonsSharingField - Array of all persons sharing the field.
 * @param {Object} params.sharedElement - The shared element object.
 * @param {Object|string} params.newValue - The new value for the field.
 * @param {Object} params.state - The current Zion SLS state object.
 * @param {Object} params.r2State - The R2 state object.
 * @param {Function} params.handleSave - The function to handle saving the dispatches.
 */
export const oldHandleSubmitLogic = ({
  values,
  selectedPerson,
  allPersonsSharingField,
  sharedElement,
  newValue,
  state,
  r2State,
  handleSave,
}) => {
  const selectedPersonSuperEl = getSuperEl(selectedPerson, sharedElement)

  const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: R2AppName }
  let fieldValue = createFieldValue(typeof newValue === 'object' ? newValue.originalText : newValue, attribution)
  if (newValue.standard) {
    fieldValue = createStandardizedFieldValue(newValue, sharedElement.fieldType, attribution)
  }

  // e.g. [ { id: '1:1:etc', isChecked: true }, ...]
  const personCheckboxValues = Object.entries(values)
    .filter(([key]) => key.startsWith('1:1:'))
    .map(([key, value]) => ({ id: key, isChecked: Boolean(value) }))
    .sort((a) => (a.id === selectedPerson.id ? -1 : 1)) // put selected P first

  // eslint-disable-next-line no-warning-comments
  // TODO: this is likely to change after prev/next images' data is loaded
  const allPersonsSharingFieldAreChecked = personCheckboxValues.every((p) => p.isChecked)
  let firstPersonDispatches
  let newFieldId
  // 1) create and attach new surname to existing NAME (only if not all persons sharing field are checked - otherwise update field value)
  if (!allPersonsSharingFieldAreChecked) {
    if (sharedElement.fieldType === 'NAME_SURN') {
      firstPersonDispatches = batchCreateSurname({
        partitionKey: selectedPerson?.partitionKey,
        nameSuperId: selectedPersonSuperEl?.id,
        surnameFieldValue: fieldValue,
      })
      newFieldId = firstPersonDispatches.find(
        (d) => d.type === 'CREATE_ELEMENT' && d?.value?.elementType === 'NAME_SURNAME'
      )?.value?.id
    } else {
      newFieldId = getNextUuid()
      firstPersonDispatches = batchCreateBasicField({
        partitionKey: selectedPerson.partitionKey,
        superId: selectedPersonSuperEl.id,
        fieldType: sharedElement.fieldType,
        fieldValue,
        superElementType: selectedPersonSuperEl.elementType,
        id: newFieldId,
      })
    }
  } else {
    firstPersonDispatches = []
  }
  let dispatches = [...firstPersonDispatches]

  if (!allPersonsSharingFieldAreChecked) {
    personCheckboxValues
      .filter((p) => p.isChecked)
      .map((personCheckbox) => allPersonsSharingField.find((p) => p.id === personCheckbox.id))
      .forEach((person, i) => {
        // extract the unique id for the newly created surname id from firstPersonDispatches
        const superEl = getSuperEl(person, sharedElement)

        // the selected person is always checked (and the checkbox disabled) - and we handle their dispatches above
        if (i > 0) {
          // 2) attach new surname or field
          dispatches.push({
            type: 'ADD_SUB',
            value: {
              superPartitionKey: superEl.partitionKey,
              subPartitionKey: selectedPerson?.partitionKey,
              superId: superEl?.id,
              subId: newFieldId,
              elementType: superEl.elementType,
            },
          })
        }
        // 3) remove old surname
        const flattenedProcessedElements = getAllProcessedElements(state.images)
        let oldElement = sharedElement
        if (sharedElement.fieldType === 'NAME_SURN') {
          const oldSurnameId = superEl?.subElements?.find((element) => element.elementType === 'NAME_SURNAME')?.id
          const supers = getSupersByType(sharedElement, 'NAME_SURNAME', flattenedProcessedElements)

          /* Supers can be an array of elements, so we need to find the one that belongs to the person 
            we are updating using the oldSurnameId. If there is no oldSurnameId or if no super is found 
            with that oldSurnameId, default to the first element in the supers array. */
          oldElement = oldSurnameId ? supers?.find((element) => element.id === oldSurnameId) || supers[0] : supers[0]
        }
        dispatches.push({
          type: 'REM_SUB',
          value: {
            superPartitionKey: superEl.partitionKey,
            subPartitionKey: oldElement.partitionKey,
            superId: superEl?.id,
            subId: oldElement?.id,
            elementType: superEl.elementType,
          },
        })
      })
    const newSharedFieldId =
      sharedElement.fieldType !== 'NAME_SURN'
        ? newFieldId
        : dispatches.find((d) => d.type === 'CREATE_ELEMENT' && d.value.extraArgs?.fieldType === 'NAME_SURN').value.id
    dispatches = dispatches
      .concat(
        createHighlightsDispatches(state.images, state.currentImageArk, r2State.editHighlights, newSharedFieldId, false)
      )
      .concat(
        createHighlightsDispatches(
          state.images,
          state.currentImageArk,
          r2State.referenceEditHighlights,
          newSharedFieldId,
          false
        )
      )
  } else {
    // 5) if everyone that was originally linked is checked, don't do any of the above and just update the value of the field
    dispatches.push({
      type: 'ADD_FV',
      value: {
        fieldValue,
        id: sharedElement.id,
        partitionKey: sharedElement?.partitionKey,
      },
    })
    dispatches = dispatches
      .concat(createHighlightsDispatches(state.images, state.currentImageArk, r2State.editHighlights))
      .concat(createHighlightsDispatches(state.images, state.currentImageArk, r2State.referenceEditHighlights))
  }

  if (selectedPerson.recordId) {
    dispatches.push({ type: 'SET_RECORD_PERSON_IDS', value: selectedPerson.recordId })
  }

  // Clear out the localizedNames value to trigger PersonOverview to fetch it again.
  dispatches.push({
    type: 'SET_PERSON_LOCALIZED_NAME',
    value: {
      personId: r2State.selectedPersonId,
      localizedName: '',
      partitionKey: state.currentImageArk,
    },
  })

  handleSave(dispatches)
}
